import type { UploadedAttachment } from '../../api/files';
import { selectCode, selectLabel } from '../../lib/approval-select';
import type { LineSuggestionDto } from '../../api/hr-org-leader';
import type { ApprovalFormField } from '../../types/approval';
import type { ApprovalLineValue } from './ApprovalLineBlock';
import { ApprovalFormView } from './ApprovalFormView';
import { computeFormula } from '../../lib/approval-formula';
import { getNumberLocale } from '../../lib/base-i18n';
import './ApprovalNew.css';

/**
 * 기안 미리보기 — **올리기 전에 완성된 문서를 본다** (2026-09-01 · 판 13 ③).
 *
 * ## ★★★ 왜 필요했나
 *
 * 종전에는 미리보기가 **없었다.** 「올리기」를 누르는 순간이 곧 처음 보는 순간이라,
 * 결재선이 맞는지·빠뜨린 칸이 없는지를 **올린 뒤에** 알았다. 회수하면 상대의 결재함에
 * 이미 흔적이 남는다.
 *
 * ## ★★ 미리보기 = 인쇄 모습
 *
 * 둘을 따로 만들지 않는다. 이 조각이 곧 종이에 나가는 모습이고, `@media print` 가 이것만 남긴다.
 * (증명서·결재 상세가 쓰는 어법 그대로 — `visibility` 로 그 조각만 남긴다.)
 *
 * ## ★★ 저장하지 않는다
 *
 * 임시저장은 담을 표가 없다 — 이 판에서도 **안 만든다**. 미리보기는 화면 안에서만 모습을
 * 바꾼다(같은 값, 다른 옷). 그래서 새 API 가 **하나도 없다** — 재료는 이미 화면이 다 갖고 있다.
 *
 * ## ★ 결재란은 «될 모습»이다
 *
 * 아직 아무도 안 찍었으니 기안 도장 하나만 실물이고 나머지는 **자리**다.
 * 찍힌 것처럼 보이면 안 된다.
 */

export interface PreviewProps {
  typeName: string;
  title: string;
  content: string;
  drafterName: string;
  belong: string;
  today: string;
  line: ApprovalLineValue;
  suggestion: LineSuggestionDto | null;
  fields: ApprovalFormField[];
  values: Record<string, unknown>;
  attachments: UploadedAttachment[];
  isUrgent: boolean;
  /** 서식 밖에서 따로 받은 크기(금액) — 서식에 전결 기준 칸이 없을 때만 선다. */
  measure: number | null;
}

const KB = 1024;
function sizeLabel(bytes: number): string {
  if (bytes < KB) return `${bytes} B`;
  if (bytes < KB * KB) return `${Math.round(bytes / KB)} KB`;
  return `${(bytes / KB / KB).toFixed(1)} MB`;
}

/**
 * 아직 못 채운 **필수** 칸들.
 *
 * ★★ 브라우저는 맨 위 하나만 짚는다 — 뭐가 더 남았는지 모른 채 하나씩 고치게 된다.
 *    여기서 **모아서** 말하고, 아래 서식에서 그 자리를 다시 짚는다.
 * ★ 계산식 칸은 사람이 적는 칸이 아니라 여기 안 센다.
 */
export function missingRequired(
  title: string, content: string, fields: ApprovalFormField[], values: Record<string, unknown>,
): string[] {
  const out: string[] = [];
  if (title.trim() === '') out.push('제목');
  if (content.trim() === '') out.push('본문');
  for (const f of fields) {
    if (f.required !== true || f.kind === 'formula') continue;
    const v = values[f.key];
    const empty = v === undefined || v === null || v === ''
      || (Array.isArray(v) && v.length === 0)
      // ★ 고르기 값은 `{code,label}` 이라 «빈 것»을 코드로 잰다 (2026-09-04 · 예산 판 B0).
      || (f.kind === 'select' && selectCode(v) === '')
      || (f.kind === 'period' && !((v as { start?: string; end?: string })?.start && (v as { end?: string })?.end));
    if (empty) out.push(f.label);
  }
  return out;
}

export function ApprovalPreview(p: PreviewProps) {
  /**
   * ★ 계산식 값은 **여기서 센다.** 기안 화면의 `formValues` 에는 안 담기기 때문이다
   *   (저장되는 값은 서버가 다시 센다 — 브라우저 값을 믿지 않는다).
   *   규칙은 서버와 **같은 조각**을 쓴다(`approval-formula.sync.test.ts` 가 잠근다).
   */
  const shown: Record<string, unknown> = { ...p.values };
  for (const f of p.fields) {
    if (f.kind !== 'formula' || !f.expr) continue;
    const r = computeFormula(f.expr, {
      field: (key) => shown[key],
      column: (fieldKey, column) => {
        const t = p.fields.find((x) => x.key === fieldKey);
        const idx = (t?.columns ?? []).indexOf(column);
        const rows = shown[fieldKey];
        if (idx < 0 || !Array.isArray(rows)) return [];
        // ★ 고르기 열은 짝이다 — 이름으로 읽는다(서버 `form-schema.ts` 와 같은 규칙 · 예산 판 B0).
        return (rows as unknown[]).map((r2) => (Array.isArray(r2) ? selectLabel(r2[idx]) : ''));
      },
    });
    if (r.value != null) shown[f.key] = r.value;
  }

  const missing = missingRequired(p.title, p.content, p.fields, shown);
  const fin = p.suggestion?.finalize;
  const num = (n: number) => n.toLocaleString(getNumberLocale());

  /** 전결로 잘렸으면 «왜 이 결재선인가» 를 숫자와 기준을 나란히 두고 말한다. */
  const finalizeNote = (() => {
    if (!p.suggestion?.finalized || !fin || fin.kind === 'NONE' || fin.threshold == null) return null;
    const measureField = p.fields.find((f) => f.measure);
    const raw = measureField ? shown[measureField.key] : p.measure;
    const v = typeof raw === 'number' ? raw : Number(raw);
    const unit = fin.kind === 'AMOUNT' ? '원' : '일';
    return Number.isFinite(v)
      ? `전결 — ${num(v)}${unit} ≤ ${num(fin.threshold)}${unit} 이라 ${fin.steps}단계에서 끝납니다.`
      : `전결 — ${num(fin.threshold)}${unit} 이하라 ${fin.steps}단계에서 끝납니다.`;
  })();

  return (
    <div className="apn__preview">
      {/* ★★ 못 채운 칸을 **모아서** 먼저 말한다 — 미리보기가 곧 점검표다. */}
      {missing.length > 0 && (
        <p className="apn__miss" role="status">
          <b>아직 못 채운 칸이 {missing.length}개 있습니다</b> — {missing.join(' · ')}.
          아래에서 붉게 짚어 두었습니다.
        </p>
      )}

      <article className="apn__paper" aria-label="미리보기">
        <div className="apn__sheettop">
          <div className="apn__ptitle">
            <h2 className={p.title.trim() === '' ? 'apn__pmiss' : undefined}>
              {p.title.trim() === '' ? '제목을 아직 안 적었습니다' : p.title}
            </h2>
            <p>{p.typeName}{p.isUrgent ? ' · 긴급' : ''}</p>
          </div>
          {/* ★ 결재란 — 기안 도장 하나만 실물이고 나머지는 «자리»다(점선). */}
          <div className="apn__appr" aria-label="결재란">
            <div className="apn__apprc">
              <div className="apn__apprh">기안</div>
              <div className="apn__apprb"><span className="apn__stamp">{p.drafterName.slice(0, 2)}</span></div>
              <div className="apn__apprn">{p.today.slice(5)}</div>
            </div>
            {p.line.approvers.slice(0, 3).map((a, i) => (
              <div className="apn__apprc" key={a.userId}>
                <div className="apn__apprh">{i + 1}단계</div>
                <div className="apn__apprb"><span className="apn__stamp is-wait">대기</span></div>
                <div className="apn__apprn">—</div>
              </div>
            ))}
          </div>
        </div>

        <div className="apn__meta">
          <div><span>기안자</span><b>{p.drafterName}</b></div>
          <div><span>소속</span><b>{p.belong}</b></div>
          <div><span>기안일</span><b>{p.today}</b></div>
        </div>

        {/* ── 결재선 — 승인 · 참고자를 각각 ── */}
        <section className="apn__psec" aria-label="결재선">
          <h3>결재선</h3>
          {p.line.approvers.length === 0 ? (
            <p className="apn__pmiss">결재자를 아직 안 골랐습니다.</p>
          ) : (
            <ol className="apn__pline">
              {p.line.approvers.map((a, i) => (
                <li key={a.userId}><b>{i + 1}</b> {a.name}{a.position ? ` · ${a.position}` : ''}</li>
              ))}
            </ol>
          )}
          {finalizeNote && <p className="apn__pnote">{finalizeNote}</p>}
          {p.line.referrers.length > 0 && (
            <p className="apn__prow"><span className="apn__ptag">참고자</span>
              {p.line.referrers.map((r) => r.name).join(' · ')}</p>
          )}
        </section>

        {/* ── 서식 — 읽는 자리와 **같은 조각**을 쓴다 ── */}
        {p.fields.length > 0 && (
          <section className="apn__psec" aria-label="서식 값">
            <ApprovalFormView fields={p.fields} data={shown} />
            {/* ★ 못 채운 필수 칸은 여기서 다시 짚는다 — 위에서 모아 말한 것과 같은 목록이다. */}
            {p.fields.filter((f) => f.required && f.kind !== 'formula' && missing.includes(f.label)).map((f) => (
              <p className="apn__pmiss" key={f.key}>«{f.label}» 을(를) 아직 안 적었습니다.</p>
            ))}
          </section>
        )}

        <section className="apn__psec" aria-label="본문">
          <h3>본문</h3>
          {p.content.trim() === ''
            ? <p className="apn__pmiss">본문을 아직 안 적었습니다.</p>
            : <p className="apn__pbody">{p.content}</p>}
        </section>

        {p.attachments.length > 0 && (
          <section className="apn__psec" aria-label="첨부">
            <h3>첨부</h3>
            <ul className="apn__pfiles">
              {p.attachments.map((a) => (
                <li key={a.fileId}>{a.name} <span>{sizeLabel(a.sizeBytes)}</span></li>
              ))}
            </ul>
          </section>
        )}
      </article>
    </div>
  );
}
